// src/cmps/HomePage/TopAnimes/TopAnimesList.jsx
import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import Slider from 'react-slick'
import { motion, LayoutGroup } from 'framer-motion'
import toast from 'react-hot-toast'
import { utilService } from '../../../services/util.service'

import {
  loadList,
  loadStatusMap,
  addItem as addItemThunk,
  removeItem as removeItemThunk,
} from '../../../store/slices/watchlist.slice.js'

import { TopAnimesPreview } from './TopAnimesPreview'
import { Button } from '../../ui/elements'
import { ArrowLeft, ArrowRight } from '../../ui/icons/shared'

export function TopAnimesList({ animeList }) {
  const [playingAnimeId, setPlayingAnimeId] = useState(null)
  const [hoveredAnimeId, setHoveredAnimeId] = useState(null)

  const isMobile = useMediaQuery({ query: '(max-width: 767px)' })
  const isTablet = useMediaQuery({
    query: '(min-width: 768px) and (max-width: 1023px)',
  })

  const dispatch = useDispatch()
  const loggedInUser = useSelector((s) => s.userModule.loggedInUser)
  const statusMap = useSelector(
    (s) => s.watchlist.statusMap?.[loggedInUser?._id] || {}
  )
  const fullWatchlist = useSelector((s) =>
    Object.values(s.watchlist.watchlist[loggedInUser?._id]?.all || {}).flat()
  )

  useEffect(() => {
    if (!loggedInUser) return
    dispatch(
      loadList({
        userId: loggedInUser._id,
        listType: 'watchlist',
        cacheKey: 'home',
        options: { limit: 150, fields: 'anizone_id,status' },
      })
    )
    const ids = animeList.map((a) => a.mal_id)
    dispatch(
      loadStatusMap({
        userId: loggedInUser._id,
        listType: 'watchlist',
        anizoneIds: ids,
      })
    )
  }, [dispatch, loggedInUser?._id, animeList])

  const isInWatch = (id) => Boolean(statusMap[id])
  const isInWatchIcon = (id) =>
    isInWatch(id) || fullWatchlist.some((i) => i.anizone_id === id)

  const handleToggleWatch = (anime) => {
    if (!loggedInUser) return toast.error('התחבר כדי לערוך רשימה')
    if (isInWatch(anime.mal_id)) {
      dispatch(
        removeItemThunk({
          userId: loggedInUser._id,
          listType: 'watchlist',
          anizoneId: anime.mal_id,
        })
      )
      toast.success('הוסר מרשימת הצפייה')
    } else {
      utilService.handleAddItem(
        'Watching',
        anime,
        dispatch,
        loggedInUser,
        [],
        addItemThunk
      )
    }
  }

  const handleTrailerClick = (anime) => {
    if (!anime.trailer?.url) {
      toast.error('לא נמצא טריילר לאנימה זו!')
      return
    }
    setPlayingAnimeId((prev) => (prev === anime.mal_id ? null : anime.mal_id))
  }

  const visibleCountDesktop = isTablet ? 3 : 7
  const gapDesktop = 16
  const baseWidthDesktop = 192
  const expandedWidthDesktop = 535
  const [windowStart, setWindowStart] = useState(0)

  const getSlideWidthDesktop = (anime) =>
    playingAnimeId === anime.mal_id ? expandedWidthDesktop : baseWidthDesktop

  useEffect(() => {
    if (playingAnimeId || isMobile) return
    const intv = setInterval(() => {
      setWindowStart((p) =>
        p < animeList.length - visibleCountDesktop ? p + 1 : 0
      )
    }, 5000)
    return () => clearInterval(intv)
  }, [animeList.length, playingAnimeId, isMobile])

  const handleNextDesktop = () => {
    if (playingAnimeId) setPlayingAnimeId(null)
    if (windowStart < animeList.length - visibleCountDesktop)
      setWindowStart(windowStart + 1)
  }

  const handlePrevDesktop = () => {
    if (playingAnimeId) setPlayingAnimeId(null)
    if (windowStart > 0) setWindowStart(windowStart - 1)
  }

  const containerStyleDesktop = {
    width:
      visibleCountDesktop * baseWidthDesktop +
      (visibleCountDesktop - 1) * gapDesktop,
    overflow: 'hidden',
    margin: '0 auto',
  }

  const sliderRef = useRef(null)
  const mobileSliderSettings = {
    dots: false,
    arrows: false,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 5000,
    cssEase: 'linear',
    pauseOnHover: true,
    pauseOnFocus: true,
    centerMode: true,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1,
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
        <h2 className="text-h2 text-softText">אנימות מובילות</h2>
        {!isMobile && (
          <div className="flex gap-2 items-center mt-2 sm:mt-0">
            <button
              className="transform transition-transform duration-300 hover:scale-105"
              title="Next"
              aria-label="Next"
              onClick={handleNextDesktop}
            >
              <ArrowRight />
            </button>
            <button
              className="transform transition-transform duration-300 hover:scale-105"
              title="Previous"
              aria-label="Previous"
              onClick={handlePrevDesktop}
            >
              <ArrowLeft />
            </button>
            <Button
              to="/animes"
              text="לכל האנימות"
              width={150}
              height={60}
              fontSize="text-lg"
              textColor="text-buttonSecondary1"
              bgColor="fill-accentLight"
              hoverBgColor="hover:scale-105 transform duration-300"
              aria-label="מעבר לעמוד כל האנימות באניזון"
              title="צפה ברשימת כל האנימות באניזון"
            />
          </div>
        )}
      </div>

      {isMobile ? (
        <Slider {...mobileSliderSettings} ref={sliderRef}>
          {animeList.map((anime) => (
            <div key={anime.mal_id} className="px-2">
              <TopAnimesPreview
                anime={anime}
                width="100%"
                isPlaying={playingAnimeId === anime.mal_id}
                isHovered={hoveredAnimeId === anime.mal_id}
                isExpanded
                onHoverEnter={() => setHoveredAnimeId(anime.mal_id)}
                onHoverLeave={() => setHoveredAnimeId(null)}
                onTrailerClick={() => handleTrailerClick(anime)}
                onToggleWatch={() => handleToggleWatch(anime)}
                isInWatchlist={isInWatchIcon(anime.mal_id)}
              />
            </div>
          ))}
        </Slider>
      ) : (
        <div style={containerStyleDesktop}>
          <LayoutGroup>
            <motion.div
              className="flex"
              style={{ gap: `${gapDesktop}px` }}
              layout
              transition={{ ease: 'easeInOut', duration: 0.4 }}
            >
              {animeList
                .slice(windowStart, windowStart + visibleCountDesktop)
                .map((anime) => (
                  <TopAnimesPreview
                    key={anime.mal_id}
                    anime={anime}
                    width={getSlideWidthDesktop(anime)}
                    isPlaying={playingAnimeId === anime.mal_id}
                    isHovered={hoveredAnimeId === anime.mal_id}
                    isExpanded={
                      getSlideWidthDesktop(anime) === expandedWidthDesktop
                    }
                    onHoverEnter={() => setHoveredAnimeId(anime.mal_id)}
                    onHoverLeave={() => setHoveredAnimeId(null)}
                    onTrailerClick={() => handleTrailerClick(anime)}
                    onToggleWatch={() => handleToggleWatch(anime)}
                    isInWatchlist={isInWatchIcon(anime.mal_id)}
                  />
                ))}
            </motion.div>
          </LayoutGroup>
        </div>
      )}
    </div>
  )
}
